import React, { useEffect, useState, useRef } from 'react';
import { Button, Alert } from 'antd';
import axios from "../common/axios";
import MathJaxHTMLContent from '../common/MathJaxHTMLContent';
import CircularLoader from "../common/CircularLoader";
import QuestionFeedback from "./QuestionFeedback";

const TrailQuestion = ({
                           step,
                           continueCurrentStep,
                           setStepUserResult,
                           showFeedback = false,
                           showResourceIds = false
                       }) => {
    const questionBlockRef = useRef();
    const fieldsetRef = useRef(); // Added ref for safe DOM manipulation

    const [loading, setLoading] = useState(true);
    const [questionUpdating, setQuestionUpdating] = useState(false);
    const [questionResult, setQuestionResult] = useState();
    const [question, setQuestion] = useState();
    const [error, setError] = useState();
    const [isAnswerDisabled, setIsAnswerDisabled] = useState(true);

    const loadQuestion = (pathwayUserId) => {
        setLoading(true);
        axios.get('/web/ui/question/trail/question', {params: { pathwayUserId: pathwayUserId},})
            .then(response => {
                setQuestion(response.data.question);
                setStepUserResult(response.data.stepUserResult);
                setError(undefined);
            })
            .catch(err => {
                setQuestion(undefined);
                setError(err.message);
            })
            .finally(() => {
                setLoading(false);
                setQuestionResult(undefined);
            });
    };

    useEffect(() => {
        if (!step?.pathwayUserId) {
            setQuestion(undefined);
            setLoading(false);
            return;
        }
        loadQuestion(step.pathwayUserId);
    }, [step]);

    useEffect(() => {
        if (fieldsetRef.current) {
            fieldsetRef.current.disabled = !!questionResult;
            if (questionResult) {
                fieldsetRef.current.classList.add('ch-question-disabled');
            } else {
                fieldsetRef.current.classList.remove('ch-question-disabled');
            }
        }
    }, [questionResult]);

    useEffect(() => {
        if (!loading && questionBlockRef.current) {
            const content = questionBlockRef.current.querySelector('#questionFormContent');
            if (content) {
                content.setAttribute('tabIndex', '-1');
                content.focus();
            }
        }
    }, [loading, question]);

    useEffect(() => {
        if (typeof window.questions === 'undefined') return;

        window.questions.initQuestions();
        if (['HIGHLIGHT', 'ALTERNATIVES', 'DRAG_DROP'].includes(question?.questionAnswerType)) {
            window.inlineQuestions?.buildQuestionTypes?.();
        }
    }, [question]);

    useEffect(() => {
        const form = questionBlockRef.current?.querySelector('form');
        if (!form || !question) {
            setIsAnswerDisabled(true);
            return;
        }

        const checkAnswerability = () => {
            const $form = $(form);
            const hasCheckedInputs = $form.find('input:checked').length > 0;
            const textInputs = $form.find('input:text');
            const allTextInputsFilled = textInputs.length > 0
                && textInputs.filter(function() { return this.value.trim() === ''; }).length === 0;
            const alternatives = $form.find('.js-q-choice');
            const isAlternativesAnswered = alternatives.length > 0 && alternatives.val();
            const isHighlightAnswered = $form.find('.js-q-highlight').length > 0
                && $form.find('.highlight-selected').length > 0;
            const isDragAndDropAnswered = $form.find('.drag-obj-blk').length > 0
                && $form.find('.drag-obj-blk > .js-drag-obj').length === 0;
            const isAnswered = hasCheckedInputs
                || allTextInputsFilled
                || isAlternativesAnswered
                || isHighlightAnswered
                || isDragAndDropAnswered;
            setIsAnswerDisabled(!isAnswered);
        };

        checkAnswerability();
        const observer = new MutationObserver(checkAnswerability);
        observer.observe(form, { attributes: true, childList: true, subtree: true });
        form.addEventListener('input', checkAnswerability);
        form.addEventListener('change', checkAnswerability);

        return () => {
            observer.disconnect();
            form.removeEventListener('input', checkAnswerability);
            form.removeEventListener('change', checkAnswerability);
        };
    }, [question]);

    const handleAnswerQuestion = () => {
        if (typeof window.questions === 'undefined' || !questionBlockRef.current) return;
        const questionBlock = $(questionBlockRef.current);
        setQuestionUpdating(true);
        window.questions.checkAnswerFeedback(
            $('.ch-question-form', questionBlock),
            questionBlock,
            '/lessonSection/checkQuestionAnswer.action',
            function (data) {
                if (data.insertAnswers) {
                    window.inlineQuestions.insertAnswers(
                        $('.ch-question-form', questionBlock),
                        data.questionResult
                    );
                }
                setQuestionResult(data);
                setQuestionUpdating(false);
            }
        );
    };

    const handleNextQuestion = () => {
        loadQuestion(step.pathwayUserId);
    };

    if (error) {
        return <Alert showIcon message={error} type="error" />;
    }

    if (loading) {
        return <CircularLoader/>;
    }

    return (
        <div ref={questionBlockRef} className="ch-question">
            <fieldset
                ref={fieldsetRef}
                aria-labelledby="questionLabel"
                className="js-questionContent"
            >
                <legend id="questionLabel" className="ch-visually-hidden">
                    Question content
                </legend>
                <div id="questionFormContent" className="ch-question-form">
                    <form
                        id="questForm"
                        method="post"
                        onSubmit={(e) => e.preventDefault()}
                        autoComplete="off"
                    >
                        <input type="hidden" name="questionId" value={question?.questionId || ''} />
                        <input type="hidden" id="prevResultInc" name="previousAnswerIncorrect" />
                        <input
                            type="hidden"
                            name="level"
                            value={question?.difficultyLevelId || '1'}
                        />
                        <input type="hidden" name="conceptId" value={step.conceptId || ''} />

                        <div>
                            {showResourceIds && (
                                <span className="resourceId js-editQuestion" data-question-id={question?.questionId}>
                                    Question: <span>{question?.questionId}</span>
                                </span>
                            )}
                            <MathJaxHTMLContent html={question?.formattedInstructions || ''} />
                        </div>

                        <div className="ch-answer-button">
                            <Button
                                type="primary"
                                onClick={handleAnswerQuestion}
                                disabled={isAnswerDisabled || loading || questionUpdating || questionResult}
                            >
                                Answer
                            </Button>
                        </div>
                    </form>
                </div>
            </fieldset>

            {questionResult && (
                <QuestionFeedback
                    result={questionResult}
                    onContinue={continueCurrentStep}
                    onNextQuestion={handleNextQuestion}
                />
            )}
        </div>
    );
};

export default TrailQuestion;